import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { profileService } from '../../services/profileService';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import ProfileInfo from './ProfileInfo';
import ProfilePictureUpload from './ProfilePictureUpload';
import PasswordReset from './PasswordReset';

const ProfileModal = ({ isOpen, onClose, userId = null }) => {
  const { user: currentUser, updateUserData } = useAuth();
  const { isMobile, isTablet, getResponsiveValue } = useTheme();
  
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [error, setError] = useState(null);

  const targetUserId = userId || currentUser?.uid;
  const isOwnProfile = targetUserId === currentUser?.uid;
  const canEdit = profileService.canEditProfile(targetUserId, currentUser);

  const sidebarWidth = getResponsiveValue({
    mobile: 'w-full',
    tablet: 'w-56',
    desktop: 'w-64'
  });

  const containerLayout = getResponsiveValue({
    mobile: 'flex-col',
    tablet: 'flex-row',
    desktop: 'flex-row'
  });

  const tabSpacing = getResponsiveValue({
    mobile: 'space-y-1',
    desktop: 'space-y-1'
  });

  const tabPadding = getResponsiveValue({
    mobile: 'px-3 py-3',
    desktop: 'px-3 py-2'
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-base',
    desktop: 'text-lg'
  });

  const subtitleSize = getResponsiveValue({
    mobile: 'text-xs',
    desktop: 'text-sm'
  });

  const tabs = [
    {
      id: 'info',
      label: 'Profile Info',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
          />
        </svg>
      )
    },
    {
      id: 'picture',
      label: 'Profile Picture',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" 
          />
        </svg>
      ),
      disabled: !canEdit
    },
    {
      id: 'security',
      label: 'Security',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
          />
        </svg>
      ),
      disabled: !isOwnProfile
    }
  ];

  useEffect(() => {
    if (isOpen && targetUserId) {
      loadProfileData();
    }
  }, [isOpen, targetUserId]);

  useEffect(() => {
    if (isOwnProfile && currentUser && isOpen) {
      console.log('ProfileModal: Using current user data');
      setProfileData(currentUser);
    }
  }, [isOwnProfile, currentUser, isOpen]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      profileService.validateProfileAccess(targetUserId, currentUser, 'view');
      
      console.log('ProfileModal: Loading profile data for', targetUserId);
      
      const data = await profileService.getProfileData(targetUserId);
      
      console.log('ProfileModal: Loaded profile data:', {
        userId: data.id,
        hasProfileData: !!data
      });
      
      setProfileData(data);
    } catch (error) {
      console.error('Error loading profile data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (updatedData) => {
    console.log('ProfileModal: Profile update received', updatedData);
    
    setProfileData(prev => {
      const updated = { ...prev, ...updatedData };
      console.log('ProfileModal: Local state updated');
      return updated;
    });
    
    if (isOwnProfile) {
      updateUserData(updatedData);
    }
    
    setTimeout(async () => {
      console.log('ProfileModal: Reloading fresh data after update');
      await loadProfileData();
    }, 500);
  };

  const handleTabChange = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab || tab.disabled) return;
    setActiveTab(tabId);
  };

  const handleClose = () => {
    setActiveTab('info');
    setError(null);
    onClose();
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" text="Loading profile..." />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <div className="bg-error-subtle border border-border-error rounded-xl p-4 mb-4">
            <div className="flex items-center justify-center">
              <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-error">{error}</span>
            </div>
          </div>
          <Button onClick={loadProfileData} variant="outline" size="sm">
            Try Again
          </Button>
        </div>
      );
    }

    if (!profileData) {
      return (
        <div className="text-center py-12">
          <div className="text-text-tertiary">No profile data available</div>
        </div>
      );
    }

    switch (activeTab) {
      case 'info':
        return (
          <ProfileInfo 
            profileData={profileData}
            canEdit={canEdit}
            isOwnProfile={isOwnProfile}
            onUpdate={handleProfileUpdate}
          />
        );
      
      case 'picture':
        return (
          <ProfilePictureUpload 
            profileData={profileData}
            userId={targetUserId}
            onUpdate={handleProfileUpdate}
          />
        );
      
      case 'security':
        return (
          <PasswordReset 
            profileData={profileData}
            isOwnProfile={isOwnProfile}
          />
        );
      
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center space-x-3">
          <div>
            <h3 className={`${titleSize} font-semibold text-text-primary`}>
              {isOwnProfile ? 'My Profile' : 
               profileData ? profileData.fullName : 'User Profile'}
            </h3>
            {profileData && (
              <div className="flex items-center space-x-2">
                <p className={`${subtitleSize} text-text-tertiary`}>{profileData.email}</p>
                {profileData.status === 'active' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-subtle text-success">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Active
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      }
      size="xl"
      maxHeight="large"
    >
      <div className={`flex ${containerLayout} h-full`}>
        <div className={`${sidebarWidth} border-r border-border-primary bg-surface-secondary ${isMobile ? 'border-b border-r-0' : ''}`}>
          <div className={getResponsiveValue({ mobile: 'p-3', desktop: 'p-4' })}>
            <nav className={tabSpacing}>
              {tabs.map((tab) => {
                const isDisabled = tab.disabled;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    disabled={isDisabled}
                    className={`
                      w-full flex items-center ${tabPadding} text-sm font-medium rounded-xl transition-colors
                      ${isActive
                        ? 'bg-navigation text-navigation-text shadow-theme-sm'
                        : isDisabled 
                          ? 'text-text-disabled cursor-not-allowed'
                          : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                      }
                    `}
                  >
                    <span className={`mr-3 ${isActive ? 'text-navigation-text' : ''}`}>
                      {tab.icon}
                    </span>
                    {tab.label}
                    {isDisabled && (
                      <svg className="w-3 h-3 ml-auto text-text-disabled" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {profileData && isOwnProfile && (
            <div className={`${getResponsiveValue({ mobile: 'p-3', desktop: 'p-4' })} border-t border-border-secondary`}>
              <div className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} font-medium text-text-tertiary mb-2`}>Profile Completion</div>
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-surface-secondary rounded-full h-2">
                  <div 
                    className="bg-accent h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${profileService.getProfileCompletionPercentage(profileData)}%` 
                    }}
                  />
                </div>
                <span className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} font-medium text-text-secondary`}>
                  {profileService.getProfileCompletionPercentage(profileData)}%
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto bg-surface-primary">
          {renderTabContent()}
        </div>
      </div>
    </Modal>
  );
};

export default ProfileModal;